import { Box, Stack } from '@mui/material';

import { PageHeader } from '@karehero/llama/pwa/atoms/PageHeader/PageHeader';

import StatusBar from '@/designs-dev/device/StatusBar';
import HomeIndicator from '@/designs-dev/device/HomeIndicator';
import ScreenRoot from '@/components/wrapped/ScreenRoot';
import AccountBar, { type Account } from '@/karehero-app/access/AccountBar';
import ClaimRow from '@/karehero-app/tell-us-once/intake/ClaimRow';
import { CHOOSE_NEXT } from '@/karehero-app/tell-us-once/intake/content';
import { PAGE_HEADER_TOP } from '@/karehero-app/shell/layout';

const SCREEN_PX = 3;

// After the review, the fork. We hold enough to start either one, and which of
// them matters more is theirs to say: one is about money and one is about care.
//
// Three, not two: the third is the way out. Neither task is urgent, and someone
// who has just finished telling us everything is allowed to want a look round
// before being handed another job. Without it the screen is a demand wearing a
// question's clothes.
//
// Navigational cards rather than a pick-one and a Next. There is no answer to
// confirm here, only a place to go, and making them choose and then confirm the
// choice would be two taps for one decision.
const ChooseNextScreen = ({
  onCalculator,
  onCare,
  onExplore,
  account,
}: {
  onCalculator?: () => void;
  /** No care assessment screens yet, so this is inert. */
  onCare?: () => void;
  /** Where exploring lands. Inert until there is somewhere to land. */
  onExplore?: () => void;
  /** The app bar. See StepScreen's own `account`. */
  account?: Account;
}) => (
  <ScreenRoot>
    <StatusBar />

    {/* The app's own bar, not a flow's. Onboarding is over by the time this
        screen appears, so this is the first screen of being in the app rather
        than a step inside something: it wears what Home wears. An AppBar would
        want a back chevron, and there is nothing behind this to go back to. */}
    {account && <AccountBar {...account} />}

    <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: SCREEN_PX, pb: 2 }}>
      <Box sx={{ pt: PAGE_HEADER_TOP }}>
        <Stack spacing={3}>
          <PageHeader title={CHOOSE_NEXT.title} variant='h2' />

          {/* The claims tab's row, because these are the same objects: a thing
              you can be part-way through, named, with a line saying what it is
              for. Drawn as a plain card here and as a ClaimRow there, the same
              claim looked like two different things depending on which screen
              you reached it from. */}
          <Stack spacing={1.5}>
            {CHOOSE_NEXT.options.map((option) => (
              <ClaimRow
                key={option.id}
                icon={option.icon}
                name={option.title}
                description={option.subtitle}
                count={
                  'filled' in option ? (option.filled as string) : undefined
                }
                onClick={
                  option.id === 'calculator'
                    ? onCalculator
                    : option.id === 'care'
                      ? onCare
                      : onExplore
                }
              />
            ))}
          </Stack>
        </Stack>
      </Box>
    </Box>

    <HomeIndicator />
  </ScreenRoot>
);

export default ChooseNextScreen;
